import { datadogRum } from "@datadog/browser-rum";
import { saveAs } from "file-saver";
import React, { useState } from "react";

import { Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { notifyError } from "@components/notifications/notifications";

export interface PayorFeedbackBannerProps {
  payorDisplayName: string;
  feedbackFileSignedUrl: string | null;
  clientInitials: string;
}

export const PayorFeedbackBanner: React.FC<PayorFeedbackBannerProps> = ({
  payorDisplayName,
  feedbackFileSignedUrl,
  clientInitials,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const hasFile = Boolean(feedbackFileSignedUrl);
  const fileName = `${clientInitials} — ${payorDisplayName} Feedback.pdf`;

  const handleDownload = async () => {
    if (!feedbackFileSignedUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(feedbackFileSignedUrl);
      if (!response.ok) throw new Error(`Feedback download responded ${response.status}`);
      const blob = await response.blob();
      saveAs(blob, fileName);
    } catch (error) {
      notifyError("Couldn't download feedback. Please try again.");
      datadogRum.addError(error, { context: "Payor feedback download failed", payorDisplayName });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-4 rounded-lg bg-orange-200 px-5 py-4"
    >
      <div className="flex flex-col gap-1">
        <Label size="small" displayType="loud" colorType="primary">
          {payorDisplayName} has requested changes to this report
        </Label>
        <Paragraph size="small" colorType="primary">
          {hasFile
            ? `Review their feedback and make the necessary edits, then resubmit it when you're ready.`
            : `Resubmit when you're ready. No detailed feedback document was attached.`}
        </Paragraph>
      </div>
      {hasFile ? (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="shrink-0 rounded-md bg-orange-800 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isDownloading ? "Downloading…" : "Download Feedback"}
        </button>
      ) : null}
    </div>
  );
};
